/*
 * Presentación del incremento 4/6.
 *
 * REGLA DURA DE ESTE ARCHIVO Y DE TODO EL MÓDULO (RNF-038): la pantalla NO
 * CONCATENA EL CONTADOR y NO DIVIDE IMPORTES. El texto "1/12", el importe imputado
 * al mes y la lista de gastos a excluir viajan YA RESUELTOS en el contrato. Si
 * aparece una interpolación que arme el contador —el número de la mensualidad, una
 * barra y el total— o una división de importes en este módulo, ES UN DEFECTO DE
 * CONSTRUCCIÓN, y hay una prueba de arquitectura que lo persigue.
 *
 * Es el mismo criterio con el que ningún cliente calcula fechas (RN-056c) ni
 * saldos (RN-097): si dos pantallas lo arman por su cuenta, una de ellas contará
 * desde cero o redondeará distinto, y el desajuste se descubre meses después en un
 * estado de cuenta.
 */

import { ESTADOS_DE_MENSUALIDAD, ESTADOS_DE_PLAN } from './api/msiApi.js';

/*
 * BARRIDO D-88 / RN-147: el módulo DEJÓ DE FORMATEAR POR SU CUENTA y usa la misma
 * función que el resto del sistema (`nucleo/importes/formatoDeImporte.js`). El
 * segundo argumento es el CÓDIGO de la divisa. El cálculo no cambió ni un centavo:
 * el reparto sigue siendo del servidor y el formato sólo presenta (RN-146g,
 * RN-147c).
 */
export { formatearImporte as importe } from '../../nucleo/importes/formatoDeImporte.js';

/** Nombre visible de cada estado del plan. La CLAVE es el valor estable para la lógica. */
export const NOMBRE_DE_ESTADO_DE_PLAN = {
  [ESTADOS_DE_PLAN.vigente]: 'Vigente',
  [ESTADOS_DE_PLAN.liquidado]: 'Liquidado',
  [ESTADOS_DE_PLAN.cancelado]: 'Cancelado',
  [ESTADOS_DE_PLAN.extinguido]: 'Extinguido por devolución',
  [ESTADOS_DE_PLAN.cerradoPorBaja]: 'Cerrado por baja de la tarjeta',
};

/**
 * Nombre visible del estado presentado de una mensualidad.
 *
 * "PAGADA" NO EXISTE Y NO DEBE EXISTIR: si el banco ya cobró y la persona ya pagó
 * depende de S-16, que sigue abierta y es del 6/6. La palabra que se muestra es
 * "Cargada" (D-57, D-67, RF-068).
 *
 * `CARGADA_HISTORICA` (RN-775, incremento 22/22): el contrato del 19/21 presenta ese
 * estado para las mensualidades que el banco ya había cobrado antes de usar la
 * aplicación, y este diccionario no tenía la clave, así que su recuadro de estado se
 * pintaba VACÍO. Dice "Cargada" —la palabra ya normada, sin texto nuevo—: una
 * histórica está cargada por construcción. LA DISTINCIÓN DE HISTÓRICA NO VIAJA POR
 * AQUÍ: viaja por las señales de la pieza compartida del 19/21 (RN-718: borde
 * punteado, relleno apagado, visto, etiqueta y leyenda), que estas pantallas pintan
 * aparte. La clave es literal y no del enum de `msiApi.js` a propósito: es el valor
 * que manda el servidor, y la corrección es de una línea en un solo archivo.
 */
export const NOMBRE_DE_ESTADO_DE_MENSUALIDAD = {
  [ESTADOS_DE_MENSUALIDAD.pendiente]: 'Pendiente',
  [ESTADOS_DE_MENSUALIDAD.cargada]: 'Cargada',
  CARGADA_HISTORICA: 'Cargada',
  [ESTADOS_DE_MENSUALIDAD.liquidada]: 'Liquidada',
  [ESTADOS_DE_MENSUALIDAD.cancelada]: 'Cancelada',
};

/**
 * Los atajos de un toque del mercado mexicano (RF-065). NO CAMBIAN con el tope
 * nuevo y es decisión tomada (D-452): son los plazos de promoción de tarjeta que
 * de verdad se ofrecen. Los atajos propios de un préstamo largo son alcance del
 * incremento 20, no de este.
 */
export const MESES_FRECUENTES = [3, 6, 9, 12, 18, 24];

export const MESES_MINIMO = 2;

/**
 * EL TOPE DE PLAZO, PIEZA ÚNICA DEL FRONTEND (D-444, RN-759, incremento 22/22):
 * 360 pagos, treinta años por doce. Antes 48. El servidor lo valida con su propia
 * pieza única (`ReglasDePlanDeMSI.MesesMaximo`) y es él quien manda: esta constante
 * existe para que el campo numérico y su ayuda digan lo mismo que el servidor, no
 * para decidir nada.
 */
export const MESES_MAXIMO = 360;

/**
 * UMBRAL DEL PLIEGUE POR AÑO (RN-767): con MÁS de 24 mensualidades el calendario
 * se agrupa por año calendario; con 24 o menos se pinta completo, exactamente como
 * hasta hoy —los planes de tarjeta típicos no cambian de aspecto—.
 *
 * El pliegue es SOLO PRESENTACIÓN (D-451): el dato que llega del servidor no
 * cambia, y ninguna cifra del pliegue entra a saldo, diferido, Vencen ni pago
 * esperado (RN-714, RN-772).
 */
export const UMBRAL_DE_PLIEGUE_POR_ANIO = 24;

/**
 * LEYENDA DE D-72 / RN-121e. Un renglón imputado de un plan cuya tarjeta se
 * desactivó SIGUE SUMANDO en el gasto del mes —esa es la decisión— y la leyenda
 * existe justamente para que nadie crea que es algo pendiente de pagar.
 */
export const LEYENDA_TARJETA_DESACTIVADA = 'Tarjeta desactivada · ya pagado';

/**
 * LEYENDA DE D-73 / RN-123c. Un total futuro y uno pasado NO SE COMPARAN COMO
 * IGUALES, y presentarlos idénticos invita justo a eso.
 */
export const LEYENDA_PROYECCION = 'Proyección: lo que está por cobrarse';

/**
 * LEYENDA DE D-53 / RN-103c. El saldo del plan es un REPARTO DE DEUDA YA CONTADA,
 * NO DEUDA ADICIONAL: se etiqueta como parte de la deuda ya mostrada y nunca se
 * suma junto a ella.
 */
export const LEYENDA_REPARTO_DE_DEUDA =
  'Es parte de la deuda que ya ves en la tarjeta, no algo adicional.';

/** Un plan cerrado por la baja de la tarjeta es de SOLO CONSULTA (RN-119b). */
export const LEYENDA_PLAN_CERRADO_POR_BAJA =
  'Se dio de baja la tarjeta con saldo en cero: no queda nada por pagar.';
